import { FC, useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { Button, Input, InputMask } from 'components/ui'
import { emailRegex } from 'helpers/regex'
import { isValidPersonDocument } from 'helpers/document-validator'
import type { CreateUserProps } from '.'
import s from './create-user.module.css'

const SignUp: FC<CreateUserProps> = ({ onSubmit, onCancel }) => {
  const {
    register,
    handleSubmit,
    formState: { errors, touched, isSubmitting },
    getValues,
    setError,
    clearErrors,
  } = useForm({
    mode: 'all',
  })

  const [isExemptState, setIsExemptState] = useState(false)
  const [isPerson, setIsPerson] = useState(true)

  const handleChangeExemptState = () => {
    setIsExemptState(!isExemptState)
  }

  const onChangeValue = (event: any) => {
    if (event.target.value === 'PF') {
      setIsPerson(true)
      return
    }
    setIsPerson(false)
  }

  useEffect(() => {
    clearErrors()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPerson])

  const handleFormSubmit = (data: any) => {
    const { password, confirmPassword, name, birthday } = data
    const personType = isPerson ? 'PF' : 'PJ'

    if (password !== confirmPassword) {
      setError('confirmPassword', {
        message: 'As senhas não são compatíveis',
      })
      return
    }

    const userName: string = name.trim()

    if (!userName.includes(' ')) {
      setError('name', {
        message: `Informe ${isPerson ? 'o nome completo' : 'a razão social'}`,
      })
      return
    }
    

    let doIt: boolean = true
    if (birthday && birthday !== '') {
      const reg = new RegExp(
        '^(((0[1-9]|[12][0-9]|30)[-/]?(0[13-9]|1[012])|31[-/]?(0[13578]|1[02])|(0[1-9]|1[0-9]|2[0-8])[-/]?02)[-/]?[0-9]{4}|29[-/]?02[-/]?([0-9]{2}(([2468][048]|[02468][48])|[13579][26])|([13579][26]|[02468][048]|0[0-9]|1[0-6])00))$',
      )
      const text = birthday.split('/').join('')

      if (!reg.test(text)) {
        doIt = false
        setError('birthday', {
          message: 'Data de nascimento inválida: Ex.: 01/01/1999',
        })
      }
    }

    if (doIt) {
      onSubmit({
        ...data,
        document: data.document.replace(/[^0-9]+/g, ''),
        phone: data.phone.replace(/[^0-9]+/g, ''),
        insEstadual: data.insEstadual?.replace(/[^0-9]+/g, ''),
        personType,
        isExemptState,
      })
    }
  }

  return (
    <div className={s.root}>
      <h1 className={s.title}>Criar conta</h1>
      <form onSubmit={handleSubmit(handleFormSubmit)} className={s.form}>
        <div>
          <div className={s.typePerson}>
            <input type="radio" value="PF" name="person" checked={isPerson === true} onChange={onChangeValue} /> Pessoa
            física
          </div>
          <div className={s.typePerson}>
            <input type="radio" value="PJ" name="person" checked={isPerson !== true} onChange={onChangeValue} /> Pessoa
            jurídica
          </div>
        </div>
        <InputMask
          mask={isPerson ? '999.999.999-99' : '99.999.999/9999-99'}
          type="tel"
          name="document"
          placeholder={isPerson ? 'CPF' : 'CNPJ'}
          error={errors?.document?.message}
          touched={touched?.document}
          disabled={isSubmitting}
          ref={register({
            required: {
              value: true,
              message: `Por favor, preencha seu ${isPerson ? 'CPF' : 'CNPJ'} no campo acima`,
            },
            validate: {
              validDocument: (value) => {
                if (!isPerson) return true
                const userDocument = value.replace(/[^0-9]+/g, '')
                return isValidPersonDocument(userDocument) || 'CPF inválido'
              },
            },
          })}
        />
        <Input
          type="text"
          name="name"
          placeholder={isPerson ? 'Nome' : 'Razão social'}
          disabled={isSubmitting}
          error={errors?.name?.message}
          touched={touched?.name}
          maxLength={100}
          ref={register({
            required: {
              value: true,
              message: `Por favor, preencha ${isPerson ? 'seu nome' : 'sua razão social'} no campo acima`,
            },
            minLength: {
              value: 5,
              message: 'Ao menos 5 (cinco) caracteres',
            },
          })}
        />

        {!isPerson && (
          <Input
            type="text"
            name="fantasyName"
            placeholder="Nome fantasia"
            disabled={isSubmitting}
            error={errors?.fantasyName?.message}
            touched={touched?.fantasyName}
            maxLength={100}
            ref={register({
              required: {
                value: true,
                message: 'Por favor, preencha nome fantasia no campo acima',
              },
              minLength: {
                value: 5,
                message: 'Ao menos 5 (cinco) caracteres',
              },
            })}
          />
        )}

        {!isPerson && (
          <>
            <div className={s.labelCheckBox}>
              <label htmlFor="iptInscricaoEstadual" className={s.label}>
                <input
                  type="checkbox"
                  name="iptInscricaoEstadual"
                  checked={isExemptState}
                  onChange={handleChangeExemptState}
                />
                Isento de inscrição estadual
              </label>
            </div>
            {!isExemptState && (
              <Input
                type="tel"
                name="insEstadual"
                placeholder="Inscrição estadual"
                disabled={isSubmitting}
                error={errors?.insEstadual?.message}
                touched={touched?.insEstadual}
                maxLength={25}
                ref={register({
                  required: {
                    value: true,
                    message: 'Por favor, preencha a inscrição estadual no campo acima',
                  },
                  minLength: {
                    value: 5,
                    message: 'Ao menos 5 (cinco) caracteres',
                  },
                })}
              />
            )}
            <Input
              type="text"
              name="nameCompany"
              placeholder="Nome responsável"
              disabled={isSubmitting}
              error={errors?.nameCompany?.message}
              touched={touched?.nameCompany}
              maxLength={140}
              ref={register({
                required: {
                  value: true,
                  message: 'Por favor, preencha o nome do responsável no campo acima',
                },
                minLength: {
                  value: 5,
                  message: 'Ao menos 5 (cinco) caracteres',
                },
              })}
            />
          </>
        )}

        <Input
          type="email"
          name="email"
          placeholder="E-mail"
          disabled={isSubmitting}
          error={errors?.email?.message}
          touched={touched?.email}
          ref={register({
            required: {
              value: true,
              message: 'Por favor, preencha seu e-mail',
            },
            pattern: {
              value: emailRegex,
              message: 'E-mail inválido',
            },
          })}
        />
        <InputMask
          mask="(99) 99999-9999"
          type="tel"
          name="phone"
          placeholder="Celular"
          error={errors?.phone?.message}
          touched={touched?.phone}
          ref={register({
            required: {
              value: true,
              message: 'Por favor, preencha seu número de celular',
            },
          })}
        />

        {isPerson && (
          <InputMask
            mask="99/99/9999"
            name="birthday"
            placeholder="DD/MM/AAAA"
            error={errors?.birthday?.message}
            touched={touched?.birthday}
            ref={register({
              required: {
                value: isPerson,
                message: 'Por favor, preencha a data de nascimento',
              },
            })}
          />
        )}

        <Input
          type="password"
          name="password"
          placeholder="Senha"
          error={errors?.password?.message}
          touched={touched?.password}
          ref={register({
            required: {
              value: true,
              message: 'Por favor, preencha a senha',
            },
            minLength: {
              value: 8,
              message: 'No mínimo 8 (oito) caracteres',
            },
            validate: {
              matchesNextPassword: (value) => {
                const { confirmPassword } = getValues()
                if (confirmPassword) {
                  return confirmPassword !== value ? 'A senha precisa ser igual' : true
                }
                return true
              },
            },
          })}
        />
        <Input
          type="password"
          name="confirmPassword"
          placeholder="Senha de confirmação"
          error={errors?.confirmPassword?.message}
          touched={touched?.confirmPassword}
          disabled={isSubmitting}
          ref={register({
            required: {
              value: true,
              message: 'Confirme a senha',
            },
            minLength: {
              value: 8,
              message: 'No mínimo 8 (oito) caracteres',
            },
            validate: {
              matchesPreviousPassword: (value) => {
                const { password } = getValues()
                return password !== value ? 'A senha precisa ser igual' : true
              },
            },
          })}
        />
        <Button variant="primary" type="submit" disabled={isSubmitting} loading={isSubmitting}>
          Criar conta
        </Button>
        <button type="button" className={s.back} onClick={onCancel}>
          Voltar
        </button>
      </form>
    </div>
  )
}

export default SignUp
